<template>
  <a-select
    :value="value"
    :placeholder="placeholder"
    :style="selectStyle"
    @change="handleChange"
    show-search
    :filter-option="filterOption"
    :disabled="disabled"
    :allow-clear="allowClear"
    :get-popup-container="(trigger) => trigger.parentNode"
  >
    <a-select-option
      v-for="bank in sortedBanks"
      :key="bank.code"
      :value="bank.name"
      :title="bank.name"
    >
      <div class="bank-option">
        <img
          :src="getBankLogo(bank.code)"
          :alt="bank.name"
          class="bank-logo"
          @error="handleLogoError"
        />
        <span class="bank-fallback" style="display: none">🏦</span>
        <span class="bank-name">{{ bank.name }}</span>
      </div>
    </a-select-option>
  </a-select>
</template>

<script>
export default {
  name: "BankSelect",

  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Nom de la banque",
    },
    size: {
      type: String,
      default: "default",
    },
    selectStyle: {
      type: Object,
      default: () => ({ width: "100%" }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      tunisianBanks: [
        { code: "STB", name: "STB - Société Tunisienne de Banque" },
        { code: "BIAT", name: "BIAT - Banque Internationale Arabe de Tunisie" },
        { code: "ATB", name: "ATB - Banque Arabe Tunisienne" },
        {
          code: "UBCI",
          name: "UBCI - Union Bancaire pour le Commerce et l'Industrie",
        },
        { code: "BNA", name: "BNA - Banque Nationale Agricole" },
        { code: "BT", name: "BT - Banque de Tunisie" },
        { code: "AMEN", name: "AMEN - Banque Amen" },
        { code: "ATTIJARI", name: "ATTIJARI - Banque Attijari" },
        {
          code: "CFCT",
          name: "CFCT - Caisse des Crédits aux Collectivités Territoriales",
        },
        { code: "BTS", name: "BTS - Banque Tunisienne de Solidarité" },
        { code: "TSB", name: "TSB - Banque Tunisienne Saoudienne" },
        { code: "QNB", name: "QNB - Banque Nationale du Qatar" },
        { code: "BH", name: "BH - Banque de l'Habitat" },
        { code: "BTE", name: "BTE - Banque Tunisienne des Emirats" },
        { code: "STUSID", name: "STUSID - Banque Stusid" },
        { code: "ZITOUNA", name: "ZITOUNA - Banque Zitouna" },
        { code: "ALBARAKA", name: "ALBARAKA - Banque Al Baraka Islamique" },
        { code: "Kuwait", name: "Kuwait - Maison de Finance du Koweït" },
        { code: "ABC", name: "ABC - Corporation Bancaire Arabe" },
        { code: "CITI", name: "CITI - Citibank Tunisie" },
        { code: "BNP", name: "BNP - BNP Paribas" },
        { code: "SG", name: "SG - Société Générale Tunisie" },
        { code: "CA", name: "CA - Crédit Agricole Tunisie" },
        { code: "HSBC", name: "HSBC - HSBC Tunisie" },
        { code: "UIB", name: "UIB - Union Internationale de Banques" },
        { code: "BTL", name: "BTL - Banque Tuniso-Libyenne" },
        { code: "ALWIFAK", name: "ALWIFAK - Banque Al Wifak" },
        { code: "BTK", name: "BTK - Banque Tuniso-Koweïtienne" },
        { code: "BTC", name: "BTC - Banque Tunisienne du Commerce" },
        { code: "TIB", name: "TIB - Banque Internationale de Tunis" },
        { code: "TFB", name: "TFB - Banque Étrangère Tunisienne" },
        {
          code: "NAIB",
          name: "NAIB - Banque Internationale d'Afrique du Nord",
        },
        { code: "WIB", name: "WIB - Banque Internationale Wifak" },
        { code: "BFPME", name: "BFPME - Banque de Financement des PME" },
        { code: "CITI_OFF", name: "CITI_OFF - Succursale Offshore Citibank" },
        { code: "BFT", name: "BFT - Banque Franco-Tunisienne" },
        { code: "BCT", name: "BCT - Banque Centrale de Tunisie" },
      ],
    };
  },

  computed: {
    sortedBanks() {
      return [...this.tunisianBanks].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
  },

  methods: {
    getBankLogo(bankCode) {
      // Using local bank logo files with correct extensions
      const basePath = "/resources/images/logos/banks/";
      const bankLogos = {
        STB: `${basePath}stb.jpg`,
        BIAT: `${basePath}biat.jpg`,
        ATB: `${basePath}atb.jpg`,
        UBCI: `${basePath}ubci.png`,
        BNA: `${basePath}bna.jpeg`,
        BT: `${basePath}bt.jpg`,
        AMEN: `${basePath}amen.jpeg`,
        ATTIJARI: `${basePath}attijari.png`,
        CFCT: `${basePath}cfct.png`,
        BTS: `${basePath}bts.png`,
        TSB: `${basePath}tsb.png`,
        QNB: `${basePath}qnb.jpg`,
        BH: `${basePath}bh.png`,
        BTE: `${basePath}bte.jpeg`,
        STUSID: `${basePath}stusid.jpeg`,
        ZITOUNA: `${basePath}zitouna.png`,
        ALBARAKA: `${basePath}albaraka.png`,
        Kuwait: `${basePath}kuwait.png`,
        ABC: `${basePath}abc.png`,
        CITI: `${basePath}citi.png`,
        BNP: `${basePath}bnp.png`,
        SG: `${basePath}sg.png`,
        CA: `${basePath}ca.png`,
        HSBC: `${basePath}hsbc.jpg`,
        UIB: `${basePath}uib.jpeg`,
        BTL: `${basePath}btl.png`,
        ALWIFAK: `${basePath}alwifak.png`,
        BTK: `${basePath}btk.png`,
        BTC: `${basePath}btc.png`,
        TIB: `${basePath}tib.png`,
        TFB: `${basePath}tfb.png`,
        NAIB: `${basePath}naib.jpeg`,
        WIB: `${basePath}wib.png`,
        BFPME: `${basePath}bfpme.jpeg`,
        CITI_OFF: `${basePath}citi-off.png`,
        BFT: `${basePath}bft.png`,
        BCT: `${basePath}bct.jpeg`,
      };
      return bankLogos[bankCode] || "🏦";
    },

    handleLogoError(event) {
      // Fallback to a simple bank emoji if logo fails to load
      event.target.style.display = "none";
      event.target.nextElementSibling.style.display = "inline";
    },

    handleChange(value) {
      this.$emit("input", value);
      this.$emit("change", value);
    },

    filterOption(input, option) {
      // Get the bank name from the option's value (which is the bank name)
      const bankName = option.componentOptions.propsData.value || "";
      return bankName.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
  },
};
</script>

<style scoped>
.bank-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bank-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 4px;
  background-color: #f5f5f5;
  padding: 2px;
}

.bank-fallback {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.bank-name {
  flex: 1;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* Custom styling for the select dropdown */
:deep(.ant-select-item-option-content) {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 250px;
}

/* Ensure dropdown has consistent width */
:deep(.ant-select-dropdown) {
  min-width: 300px;
}

/* Style for the selected value display */
:deep(.ant-select-selection-item) {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.ant-select-selection-item .bank-logo) {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

:deep(.ant-select-selection-item .bank-name) {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

/* Hover effect for better UX */
:deep(.ant-select-item-option:hover .bank-option) {
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
}

/* Selected option styling */
:deep(.ant-select-item-option-selected .bank-option) {
  background-color: #e6f7ff;
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
}
</style>
